import { trackEvent } from 'app/shared/utils/analytics/trackEvent';
import { gaEvents } from 'app/shared/constants/AnalyticsConstants';
import { getCLS, getFID, getLCP } from 'web-vitals';

type SendWebVitalsToAnalyticsArgs = {
  name: string;
  value: number;
  id: string;
};

type PerfEventType = {
  action: SendWebVitalsToAnalyticsArgs['name'];
  label: SendWebVitalsToAnalyticsArgs['id'];
  value: SendWebVitalsToAnalyticsArgs['value'];
  nonInteraction: boolean;
};

/**
 * Initializes Google Web Vitals 'Cumulative Layout Shift', 'First Input Delay',
 * 'Largest Contentful Paint', and sends the metrics to Google Analytics and
 * ZG Analytics.
 * @param {func} dispatchFunc - Redux dispatch function
 */
const webVitals_initialize = () => {
  const sendWebVitalsToAnalytics = ({ name, value, id }: SendWebVitalsToAnalyticsArgs) => {
    const perfEvent: PerfEventType = {
      /**
       * The action is the name of the perf metric to be recorded
       * CLS | FID | LCP
       */
      action: name,
      /**
       * The `id` value will be unique to the current page load. When sending
       * multiple values from the same page (e.g. for CLS), Google Analytics can
       * compute a total by grouping on this ID (note: requires `eventLabel` to
       * be a dimension in your report).
       */
      label: id,
      /**
       * Google Analytics metrics must be integers, so the value is rounded.
       * For CLS the value is first multiplied by 1000 for greater precision
       * (note: increase the multiplier for greater precision if needed).
       */
      value: Math.round(name === 'CLS' ? value * 1000 : value),
      /**
       * Use a non-interaction event to avoid affecting bounce rate.
       */
      nonInteraction: true,
    };

    trackEvent(gaEvents.WEB_VITALS_PERF_METRICS, perfEvent);
  };
  // Cumulative Layout Shift
  getCLS(sendWebVitalsToAnalytics);

  // First Input Delay - May not be reported for some page loads @see https://github.com/GoogleChrome/web-vitals#getfid
  getFID(sendWebVitalsToAnalytics);

  // Largent Contentful Paint
  getLCP(sendWebVitalsToAnalytics);
};

export { webVitals_initialize };
